import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import AuthShell, { Field, describeAuthError } from '../components/AuthShell.tsx';
import { accountApi } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function Forgot() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useDocumentTitle('Reset your password');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await accountApi.forgot(email);
      setSent(true);
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  /*
   * Same screen either way, and unlike signup this one has to stay conditional:
   * there is no account to notify, and mailing every address anyone types would
   * make this a way to send strangers post.
   */
  if (sent) {
    return (
      <AuthShell
        eyebrow="Check your email"
        title="Check your inbox"
        intro={
          <>
            If <strong className="text-chalk">{email}</strong> has an account, a reset link is on
            its way. It works once, and not for long.
          </>
        }
      >
        <Link to="/signin" className="btn btn-ghost justify-self-start">
          Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Forgot your password"
      title="Get a reset link"
      intro="We will email you a link to choose a new one."
    >
      <form onSubmit={onSubmit} className="grid gap-5">
        <Field
          id="forgot-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button className="btn btn-primary justify-self-start" disabled={busy || !email}>
          {busy ? 'Sending…' : 'Email me a link'}
        </button>
      </form>

      <p className="text-sm text-muted">
        <Link to="/signin" className="text-petal hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  );
}
